'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  GridPattern,
} from '@baseball-near-you/ui';
import { SanityImageComponent } from '@bb/components/Marketing/SanityImageComponent';
import { ClickEventType } from '@bb/components/TrackingButton/types';
import { getAdBannerAnalyticsProperties } from '@bb/lib/posthog/utils';
import { InternalLinkPaths, LinkType } from '@bb/lib/types';
import type { AdBanner, Link as ILink } from '@bb/sanity.types';
import type { Route } from 'next';
import Link from 'next/link';
import posthog from 'posthog-js';
import { type ReactNode, useEffect, useState } from 'react';
import { AdvertContactForm } from '../AdvertContactForm';

export function AdvertisementBanner({ adBanner }: { adBanner: AdBanner }) {
  const AdBannerComponent = adBanner.placeholderContactForm
    ? InternalBlogAdvertisementPlaceholder
    : BlogAdvertisement;

  return <AdBannerComponent adBanner={adBanner} />;
}

export function InternalBlogAdvertisementPlaceholder(props: {
  adBanner?: AdBanner;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className="w-full">
        {props.adBanner ? (
          <BlogAdvertisement adBanner={props.adBanner} />
        ) : (
          <div className="relative mb-3 flex h-[150px] items-center justify-center rounded-sm border border-gray-200 bg-gray-100">
            <p className="title z-20 select-none px-3 py-1 text-7xl! text-gray-400!">
              your ad here
            </p>
            <GridPattern
              height={150 / 8}
              width={20}
              className="stroke-gray-300/30! blur-xs"
            />
          </div>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Advertise on Baseball Near You!</DialogTitle>
        <DialogDescription>
          Purchasing advertising space on "Baseball Near You" blog posts offers
          a unique opportunity to reach a dedicated audience interested in youth
          travel baseball.
        </DialogDescription>
        <AdvertContactForm onSuccess={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}

export function BlogAdvertisementImageRenderer(props: { adBanner: AdBanner }) {
  return (
    <>
      {/*
        This is a hack to get the ad banner to render correctly on mobile or desktop
        without a flash of the wrong image size.
        Next.js renders the page on the server first, and then the client
        so its impossible to know the screen size before the client renders.
      */}
      {props.adBanner.mobileImage && (
        <SanityImageComponent
          className="block md:hidden"
          style={{ borderRadius: '3px', width: '100%' }}
          value={props.adBanner.mobileImage}
        />
      )}
      <SanityImageComponent
        className="hidden md:block"
        style={{ borderRadius: '3px', width: '100%' }}
        value={props.adBanner.image}
      />
    </>
  );
}

export const getAdBannerLinkUrl = (link: ILink[number]): Route<string> => {
  if (link?._type === LinkType.External) {
    return link.external!;
  }

  if (link._type === LinkType.InternalNonMarketingPage && link.internal) {
    return InternalLinkPaths[link.internal]!;
  }

  if ((link._type as any) === LinkType.BlogPage && (link as any).slug.current) {
    return `/blog/${(link as any).slug.current}`;
  }

  if (
    (link._type as any) === LinkType.InternalMarketingPage &&
    (link as any).slug.current
  ) {
    return `/${(link as any).slug.current}`;
  }

  return '';
};

const WrapperLink = ({
  children,
  adBanner,
}: {
  children: ReactNode;
  adBanner: AdBanner;
}) => {
  // If this is a placeholder contact form, we don't need a link
  if (adBanner.placeholderContactForm) {
    return children;
  }

  const link = adBanner.link as any as ILink[number] | undefined;
  const linkType = link?._type as LinkType | undefined;

  // If no link is provided and this isn't a placeholder form, just render children
  if (!link || !linkType) {
    return null;
  }

  const trackClick = () => {
    posthog.capture(
      ClickEventType.AdBannerClicked,
      getAdBannerAnalyticsProperties({
        id: adBanner._id,
        name: adBanner.name,
        linkType: linkType,
        linkUrl: getAdBannerLinkUrl(link),
        hasContactForm: !!adBanner.placeholderContactForm,
      })
    );
  };

  if (link._type === LinkType.External) {
    return (
      <a
        onClick={trackClick}
        href={link.external}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  if (
    [
      LinkType.InternalNonMarketingPage,
      LinkType.BlogPage,
      LinkType.InternalMarketingPage,
    ].includes(linkType)
  ) {
    return (
      <Link href={getAdBannerLinkUrl(link)} onClick={trackClick}>
        {children}
      </Link>
    );
  }

  return children;
};

export function BlogAdvertisement({ adBanner }: { adBanner: AdBanner }) {
  useEffect(() => {
    const link = adBanner.link as any as ILink[number] | undefined;
    const linkType = link?._type as LinkType | undefined;

    if (!link || !linkType) return;

    posthog.capture(
      'ad_banner_mounted',
      getAdBannerAnalyticsProperties({
        id: adBanner._id,
        name: adBanner.name,
        linkType: linkType,
        linkUrl: getAdBannerLinkUrl(link),
        hasContactForm: !!adBanner.placeholderContactForm,
      })
    );
  }, []);

  return (
    <WrapperLink adBanner={adBanner}>
      <BlogAdvertisementImageRenderer adBanner={adBanner} />
    </WrapperLink>
  );
}
